import { useEffect, useState } from 'react';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import moment from 'moment';
import { useListContext } from 'framework/list';
import { COMPUTE_PERFORMANCE_BASED_BONUS, useExpenseService } from 'domain/shared/expense';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { DateInput } from 'framework/form';

type Props = {
  disabledInput?: boolean;
  initialValue?: string;
  [key: string]: any;
};

const MigrateExpenseAccrualsButton = ({ disabledInput = false, initialValue = '', ...props }: Props) => {
  const { migrateBatchExpenseAccruals } = useExpenseService();
  const [dateErrors, setDateErrors] = useState<string[]>([]);
  const { getToday } = useDateService();
  const { filter, reloadData = () => {} } = useListContext();

  const currentFilterValue = filter?.period?.eq;
  const currentMonth = getToday().startOf('month').subtract(1, 'month');

  const [date, setDate] = useState<any>(initialValue || currentFilterValue || currentMonth);

  const onDateChange = (values: any) => {
    setDate(values.date);

    const isValid = moment(values.date, DATE_FORMAT.API_ONLY_MONTH, true).isValid();

    if (!values.date) {
      setDateErrors(['Field is required']);
    } else if (!isValid) {
      setDateErrors(['Please, choose the correct date']);
    } else if (moment(values.date, DATE_FORMAT.API_ONLY_MONTH, true).isAfter(currentMonth)) {
      setDateErrors(['Please, choose month earlier than current month']);
    } else {
      setDateErrors([]);
    }
  };

  useEffect(() => {
    if (currentFilterValue) {
      onDateChange({ date: currentFilterValue });
    }
    if (initialValue) {
      onDateChange({ date: initialValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterValue, initialValue]);

  return (
    <ButtonWithConfirmation
      displayMessage="Do you really want to migrate expense accruals? Please, fill out the input below."
      modalChildren={
        <>
          <DateInput
            source="date"
            label="Period"
            onChangeCallback={onDateChange}
            errorMessages={dateErrors}
            isRequired
            dateFormat={DATE_FORMAT.API_ONLY_MONTH}
            value={
              date && moment(date, DATE_FORMAT.API_ONLY_MONTH, true).isValid()
                ? moment(date).format(DATE_FORMAT.API_ONLY_MONTH)
                : (date as any)
            }
            maxDate={currentMonth}
            disabled={disabledInput}
            sx={{ mt: 2.5 }}
          />
        </>
      }
      variant="outlined"
      submitMethod={() =>
        migrateBatchExpenseAccruals({
          period: moment(date).format(DATE_FORMAT.API_ONLY_MONTH),
        })
      }
      confirmBtnProps={{ disabled: !!dateErrors.length }}
      permissionToCheck={COMPUTE_PERFORMANCE_BASED_BONUS}
      afterSubmit={{
        successMessage: 'Expense accruals migration has been successfully started.',
        errorMessage: "Can't start expense accruals migration.",
        execute: () => {
          reloadData();
        },
      }}
      {...props}
    >
      Migrate expense accruals
    </ButtonWithConfirmation>
  );
};

export default MigrateExpenseAccrualsButton;
